/* eslint-disable @typescript-eslint/strict-boolean-expressions */
/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable @typescript-eslint/no-misused-promises */
import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { Grid, Button } from '@mui/material';
import { useSnackbar } from 'notistack';
import axios from 'axios';
import { Country, State, City } from 'country-state-city';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from 'react-query';
import StradaLoader from 'shared-components/components/StradaLoader';
import type { IDetailForm, IErrorProperty } from 'admin/AdminFormTypes';
import type { IFormValues } from 'formsTypes';
import HookTextField from 'shared-components/hooks/HookTextField';
// import Sidebar from 'admin/sidebar';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import HookSelectField from 'shared-components/hooks/HookSelectField';
import PrimayButton from 'shared-components/components/PrimayButton';
import type { ICSC } from 'admin/buildingSettings/buildings/types';
import { useSelector } from 'react-redux';
import type { RootState } from 'mainStore';

const defaultValues: IDetailForm = {
  name: '',
  address: '',
  country: '',
  state: '',
  city: '',
  company: '',
  zip: '',
  contact_first_name: '',
  contact_last_name: '',
  contact_email: '',
  total_sqft: null,
};

const schema = yup.object().shape({
  name: yup.string().required('This field is required').matches(/[A-Za-z]/, 'Please enter a valid name').matches(/^\s*\S[^]*$/, 'This field is required'),
  address: yup.string().required('This field is required').matches(/[A-Za-z]/, 'Please enter a valid address').matches(/^\s*\S[^]*$/, 'This field is required'),
  country: yup.string().required('Please select country'),
  state: yup.string().required('Please select state'),
  city: yup.string().required('Please select city'),
  company: yup.string().required('This field is required').matches(/[A-Za-z]/, 'Please enter a valid company').matches(/^\s*\S[^]*$/, 'This field is required')
    .nullable(),
  zip: yup.string().required('This field is required').matches(/^\s*\S[^]*$/, 'This field is required'),
  contact_first_name: yup.string().required('This field is required').matches(/[A-Za-z]/, 'Please enter a valid first name').matches(/^\s*\S[^]*$/, 'This field is required')
    .nullable(),
  contact_last_name: yup.string().required('This field is required').matches(/[A-Za-z]/, 'Please enter a valid last name').matches(/^\s*\S[^]*$/, 'This field is required')
    .nullable(),
  contact_email: yup.string().required('This field is required').email('Please enter your valid contact email').matches(/^\s*\S[^]*$/, 'This field is required')
    .nullable(),
});

export default function CreateBuilding(): JSX.Element {
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const currentWorkspace = useSelector(
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    (state: RootState) => state.workspaces.currentWorkspace.currentWorkspace,
  );
  const [countries, setCountries] = useState<ICSC[]>([]);
  const [states, setStates] = useState<ICSC[]>([]);
  const [cities, setCities] = useState<ICSC[]>([]);

  const capitalizeFirstLetter = (msg: string): string => msg.charAt(0).toUpperCase() + msg.slice(1);

  const { mutate, isLoading } = useMutation(async (data: IDetailForm) => axios({
    url: `api/building/?workspace=${currentWorkspace.id}`,
    method: 'POST',
    data,
  }), {
    onSuccess: () => {
      queryClient.invalidateQueries('get/buildings').catch(() => {});
      enqueueSnackbar('Created Successfully');
      navigate('/workspace/buildings');
    },
    onError: (error: IErrorProperty) => {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-explicit-any
      const message = (error as any)?.data?.response?.message as string | undefined;
      const displayMessage = message ? capitalizeFirstLetter(message) : 'Something went wrong. Please try again.';
      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
      enqueueSnackbar(displayMessage, {
        variant: 'error',
        content: (key, msg) => (
          <div className='text-white bg-danger ps-4 pe-5 py-3'>
            {msg}
          </div>
        ),
      });
    },
  });

  const {
    control, formState, handleSubmit, watch, setValue,
  } = useForm<IFormValues>({
    mode: 'onChange',
    defaultValues,
    resolver: yupResolver(schema),
  });
  const country = watch('country');
  const state = watch('state');

  useEffect(() => {
    const selectedCountries = Country.getAllCountries().map((c) => ({
      name: c.name,
      value: c.name,
      code: c.isoCode,
    }));
    setCountries(selectedCountries);
  }, []);

  useEffect(() => {
    const countryCode = countries.filter((c) => c.name === watch('country'))[0]?.code;
    if (countryCode) {
      const filteredStates = State.getStatesOfCountry(countryCode).map((ci) => ({
        name: ci.name,
        value: ci.name,
        code: ci.isoCode,
      }));
      setStates(filteredStates);
      setCities([]);
      setValue('state', '', { shouldDirty: true });
      setValue('city', '', { shouldDirty: true });
    } else {
      setStates([]);
      setCities([]);
    }
  }, [country]);

  useEffect(() => {
    const stateCode = states.filter((st) => st.name === watch('state'))[0]?.code;
    const countryCode = countries.filter((c) => c.name === watch('country'))[0]?.code;
    if (stateCode && countryCode) {
      const filterdCities = City.getCitiesOfState(countryCode, stateCode).map((cty) => ({
        name: cty.name,
        value: cty.name,
        code: cty.stateCode,
      }));
      setCities(filterdCities);
      setValue('city', '', { shouldDirty: true });
    } else {
      setCities([]);
    }
  }, [state, states]);

  const { errors } = formState;

  const onSubmit = (data: IDetailForm): void => {
    mutate(data);
  };

  const handleCancel = (): void => {
    window.history.back();
    // navigate('/dashboard');
  };

  return (
    <div style={{ display: 'flex', marginTop: '72px' }}>
      {/* <Sidebar variant='main' activeLink='' /> */}
      <StradaLoader open={isLoading} />
      <Grid
        container
        justifyContent='center'
        ml={-3}
        className='mt-4'
      >
        <Grid item sm={12} md={6}>

          <form onSubmit={handleSubmit(onSubmit)}>
            <Grid container columnSpacing={2}>
              <Grid item sm={12} className='mb-4 fw-normal d-flex align-items-center' style={{ color: 'rgba(33, 33, 33, 0.87)', fontSize: '22px' }}>
                <div aria-hidden='true' onClick={(): void => { window.history.back(); }} className='cursor-pointer me-2'>
                  <ArrowBackIcon />
                  {' '}
                </div>
                Create Building
              </Grid>
              <Grid item sm={12} md={6}>
                <HookTextField
                  name='name'
                  label='Name'
                  control={control}
                  errors={errors}
                  maxLength={150}
                />
              </Grid>
              <Grid item sm={12} md={6}>
                <HookTextField
                  name='total_sqft'
                  label='Total SF'
                  control={control}
                  errors={errors}
                  type='number'
                />
              </Grid>
              <Grid item className='building-settings-detail-sub-heading mb-3'>  Business Address</Grid>
              <Grid item sm={12}>
                <HookTextField
                  name='address'
                  label='Address'
                  control={control}
                  errors={errors}
                  maxLength={250}
                />
              </Grid>

              <Grid item sm={12}>
                <HookSelectField
                  name='country'
                  label='Country'
                  control={control}
                  errors={errors}
                  options={countries}
                />
              </Grid>

              <Grid item sm={12} md={6}>
                <HookSelectField
                  name='state'
                  label='State'
                  control={control}
                  errors={errors}
                  options={states}
                />
              </Grid>
              <Grid item sm={12} md={6}>
                <HookTextField
                  name='zip'
                  label='Post Code'
                  control={control}
                  errors={errors}
                  maxLength={150}
                />
              </Grid>
              <Grid item sm={12} md={12}>
                <HookSelectField
                  name='city'
                  label='City'
                  control={control}
                  errors={errors}
                  options={cities}
                />
              </Grid>
              <Grid item sm={12} md={12}>
                <HookTextField
                  name='company'
                  label='Company'
                  control={control}
                  errors={errors}
                  maxLength={150}
                />
              </Grid>

              <Grid item sm={12} md={12}>
                <h6 className='building-settings-detail-sub-heading'> Contact Person  </h6>
              </Grid>

              <Grid item sm={12} md={6}>
                <HookTextField
                  name='contact_first_name'
                  label='First Name'
                  control={control}
                  errors={errors}
                  maxLength={150}
                />
              </Grid>
              <Grid item sm={12} md={6}>
                <HookTextField
                  name='contact_last_name'
                  label='Last Name'
                  control={control}
                  errors={errors}
                  maxLength={150}
                />
              </Grid>
              <Grid item sm={12} md={12}>
                <HookTextField
                  name='contact_email'
                  label='Email'
                  control={control}
                  errors={errors}
                  maxLength={150}
                />
              </Grid>
              <Grid item sm={12} md={12} justifyContent='right'>
                <div className='action-btn'>
                  <Button variant='text' className='me-3 text-capitalize' onClick={handleCancel}> Cancel </Button>
                  <PrimayButton type='submit' disabled={isLoading}> Create </PrimayButton>
                </div>
              </Grid>
            </Grid>

          </form>
        </Grid>
      </Grid>
    </div>

  );
}
